type Listener = () => void;

/** Maximum number of commands to keep in history */
const MAX_COMMAND_HISTORY = 100;

/**
 * Represents a state change for a single field
 */
export interface FieldDiff {
  from: unknown;
  to: unknown;
}

/**
 * State diff showing what changed after a command
 */
export type StateDiff = Record<string, FieldDiff>;

/**
 * Represents a logged command for devtools
 */
export interface CommandLogEntry {
  id: number;
  timestamp: number;
  command: string;
  args?: Record<string, unknown>;
  diff?: StateDiff;
}

/**
 * Fields to skip when computing diffs (volatile or large fields)
 */
const SKIP_DIFF_FIELDS = new Set(["currentTimestamp", "waveforms"]);

/**
 * Compute a shallow diff between two states.
 * Skips fields in SKIP_DIFF_FIELDS.
 * For objects like 'tracks', shows which keys changed.
 */
function computeStateDiff(before: object, after: object): StateDiff | undefined {
  const beforeObj = before as Record<string, unknown>;
  const afterObj = after as Record<string, unknown>;
  const diff: StateDiff = {};

  // Get all keys from both states
  const allKeys = new Set([...Object.keys(beforeObj), ...Object.keys(afterObj)]);

  for (const key of allKeys) {
    if (SKIP_DIFF_FIELDS.has(key)) continue;

    const beforeVal = beforeObj[key];
    const afterVal = afterObj[key];

    // Skip if values are identical (reference equality for objects)
    if (beforeVal === afterVal) continue;

    // Handle tracks object specially
    if (key === "tracks" && typeof beforeVal === "object" && typeof afterVal === "object") {
      const beforeTracks = (beforeVal || {}) as Record<string, unknown>;
      const afterTracks = (afterVal || {}) as Record<string, unknown>;
      const beforeIds = Object.keys(beforeTracks);
      const afterIds = Object.keys(afterTracks);

      // Find changed tracks
      const changedTracks: string[] = [];
      for (const id of afterIds) {
        if (beforeTracks[id] !== afterTracks[id]) {
          changedTracks.push(id);
        }
      }

      // Find added/removed tracks
      const addedIds = afterIds.filter(id => !beforeIds.includes(id));
      const removedIds = beforeIds.filter(id => !afterIds.includes(id));

      if (changedTracks.length > 0 || addedIds.length > 0 || removedIds.length > 0) {
        // For changed tracks, show the actual state change
        if (changedTracks.length === 1) {
          const id = changedTracks[0];
          diff.tracks = {
            from: beforeTracks[id],
            to: afterTracks[id],
          };
        } else if (changedTracks.length > 1) {
          diff.tracks = {
            from: `${changedTracks.length} tracks`,
            to: "changed",
          };
        }

        if (addedIds.length > 0) {
          diff["tracks.added"] = { from: null, to: addedIds };
        }
        if (removedIds.length > 0) {
          diff["tracks.removed"] = { from: removedIds, to: null };
        }
      }
    } else {
      // Simple value comparison
      diff[key] = { from: beforeVal, to: afterVal };
    }
  }

  return Object.keys(diff).length > 0 ? diff : undefined;
}

/**
 * CommandLog tracks engine commands for devtools.
 * Uses a snapshot pattern for React compatibility with useSyncExternalStore.
 */
export class CommandLog {
  private log: CommandLogEntry[] = [];
  private snapshot: CommandLogEntry[] = [];
  private idCounter: number = 0;
  private listeners: Set<Listener> = new Set();

  /**
   * Log a command with optional args and before/after state for diff computation.
   */
  add(
    command: string,
    args?: Record<string, unknown>,
    before?: object,
    after?: object
  ): void {
    const diff = before && after ? computeStateDiff(before, after) : undefined;

    const entry: CommandLogEntry = {
      id: this.idCounter++,
      timestamp: Date.now(),
      command,
      args,
      diff,
    };
    this.log.push(entry);

    // Trim to max size
    if (this.log.length > MAX_COMMAND_HISTORY) {
      this.log = this.log.slice(-MAX_COMMAND_HISTORY);
    }

    // Create new snapshot for React
    this.snapshot = [...this.log];
    this.notifyListeners();
  }

  /**
   * Get the command log snapshot.
   * Returns a stable reference that only changes when commands are added.
   */
  getSnapshot(): CommandLogEntry[] {
    return this.snapshot;
  }

  /**
   * Subscribe to command log changes.
   */
  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Clear the command log.
   */
  clear(): void {
    this.log = [];
    this.snapshot = [];
    this.notifyListeners();
  }

  private notifyListeners(): void {
    this.listeners.forEach((listener) => listener());
  }
}
